/**
 * Filters element-group buckets down to a selection that may contain either
 * whole-group names (e.g. drilling into "Transport" scopes to all of its
 * members) or individual element/layer IDs (e.g. picking "Libraries" from the
 * Elements filter dropdown, a member of "Buildings and Property").
 *
 * A bucket is kept if either:
 * - its own label is selected — the whole group passes through unchanged, or
 * - one or more of its member IDs are selected — the bucket is narrowed to
 *   just those members.
 *
 * An empty selection means "no filter applied" and returns all buckets as-is.
 *
 * @param buckets - element-group buckets, each `{ [groupLabel]: elementIds[] }`
 * @param selected - `selectedElementGroups` from query params; may contain
 *   group labels, individual element IDs, or a mix of both
 * @returns buckets filtered/narrowed to the selection; buckets with no
 *   matching members are dropped entirely
 */
export function filterElementBuckets(
  buckets: Array<Record<string, string[]>>,
  selected: string[],
): Array<Record<string, string[]>> {
  if (selected.length === 0) return buckets

  return buckets
    .map((bucket) => {
      const [label, elementIds] = Object.entries(bucket)[0]

      if (selected.includes(label)) {
        return { [label]: elementIds }
      }

      const matchedIds = elementIds.filter((id) => selected.includes(id))
      return matchedIds.length > 0 ? { [label]: matchedIds } : null
    })
    .filter((bucket): bucket is Record<string, string[]> => bucket !== null)
}
